import React, { ReactNode, useContext, useEffect } from 'react';
import { usePollContextValue, usePollStatePredicate } from '../../reducer/machine/utils';
import { PollContext } from '../../store/withContext';

export type TrackingActions = {
  handleRetry: () => void;
};
export type TrackingState = {
  retryCount: number;
  isSubmittingTracking: boolean;
  trackingError: Error | undefined;
};
const TrackingContext = React.createContext<(TrackingActions & TrackingState) | undefined>(undefined);

export function TrackingProvider({ children }: { children: ReactNode }) {
  const { interpreter } = useContext(PollContext);
  const handleRetry = () => {
    interpreter.send({ type: 'retryPixelSend' });
  };

  const retryCount = usePollContextValue('retryCount');
  const isSubmittingTracking = usePollStatePredicate('questions.screen.tracking');
  const trackingError = usePollContextValue('trackingError');

  useEffect(() => {
    const listener = (e: MouseEvent) => {
      if (e.target && e.target instanceof HTMLElement) {
        let anchorElement: HTMLAnchorElement | null = null;
        if (e.target instanceof HTMLAnchorElement) {
          anchorElement = e.target;
        } else if (e.target.closest('a')) {
          anchorElement = findAnchorElement(e.target);
        }

        if (anchorElement && anchorElement.href && !anchorElement.hasAttribute('data-cta-button')) {
          interpreter.send({ type: 'linkClick', external_url: anchorElement.href });
        }
      }
    };

    window.addEventListener('click', listener);
    window.addEventListener('contextmenu', listener);
    return () => {
      window.removeEventListener('click', listener);
      window.removeEventListener('contextmenu', listener);
    };
  }, []);

  return (
    <TrackingContext.Provider
      value={{
        handleRetry,
        retryCount,
        isSubmittingTracking,
        trackingError,
      }}
    >
      {children}
    </TrackingContext.Provider>
  );
}

export function useTrackingContext() {
  const value = useContext(TrackingContext);
  if (!value) throw new Error('TrackingContext is required!');
  return value;
}

function findAnchorElement(el: HTMLElement | null) {
  if (!el || el instanceof HTMLAnchorElement) return el;
  return findAnchorElement(el.parentElement);
}
